/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { UserAuthManager, isMockAuthEnabled } from "./authentication";
import { globalErrorType } from "./ErrorContext";
import * as consentStorage from "@/features/cookie-consent/services/consentStorage";
import * as consentEvents from "@/features/cookie-consent/utils/consentEvents";
import { getCurrentLanguage } from "@shared/i18n";
import * as validateEnv from "@shared/utils/validate-env";

/**
 * Validates OIDC configuration.
 *
 * @returns {void}
 */
export function validateOIDCConfig(): void {
  validateEnv.getRequiredEnvVar('REACT_APP_AUTHORITY_URL');
  validateEnv.getRequiredEnvVar('REACT_APP_CLIENT_ID');
}

/**
 * Performs signin redirect.
 *
 * @returns {Promise<void>}
 */
export async function performSigninRedirect(): Promise<void> {
  if (isMockAuthEnabled()) {
    throw new Error('Cannot perform signin redirect in mock mode. Use the mock authentication provider instead.');
  }
  validateOIDCConfig();
  await UserAuthManager.signinRedirect({
    extraQueryParams: {
      lng: getCurrentLanguage(),
    },
  });
}

/**
 * Resumes signin flow after consent is accepted.
 *
 * @param {Function} removeLoading - Loading remove function
 * @param {Function} setGlobalError - Error setter
 * @returns {Promise<void>}
 */
async function resumeSigninAfterConsent(
  removeLoading: () => void,
  setGlobalError: (error: globalErrorType) => void
): Promise<void> {
  try {
    await performSigninRedirect();
  } catch (error) {
    setGlobalError(error as globalErrorType);
  } finally {
    removeLoading();
  }
}

/**
 * Handles signin flow with error handling.
 * Checks CDU consent before allowing login - if consent is rejected,
 * shows the consent banner instead of proceeding with login.
 * Sets up a one-time listener for consent acceptance to automatically resume login.
 *
 * @param {Function} addLoading - Loading add function
 * @param {Function} removeLoading - Loading remove function
 * @param {Function} setGlobalError - Error setter
 * @returns {Promise<void>}
 */
export async function handleSigninFlow(
  addLoading: () => void,
  removeLoading: () => void,
  setGlobalError: (error: globalErrorType) => void
): Promise<void> {
  // Check if consent is rejected - if so, show banner and don't proceed with login
  const consent = consentStorage.getConsent();
  if (consent?.status === 'rejected') {
    removeLoading();
    consentEvents.triggerLoginBannerEvent();

    // Set up one-time listener to automatically resume login when user accepts consent
    const handleConsentAcceptedForLogin = () => {
      // Remove listener after it fires once
      window.removeEventListener(consentEvents.CDU_CONSENT_ACCEPTED_FOR_LOGIN_EVENT, handleConsentAcceptedForLogin);

      // Resume login flow directly after consent is accepted
      void resumeSigninAfterConsent(removeLoading, setGlobalError);
    };

    window.addEventListener(consentEvents.CDU_CONSENT_ACCEPTED_FOR_LOGIN_EVENT, handleConsentAcceptedForLogin);
    return;
  }

  try {
    addLoading();
    await performSigninRedirect();
  } catch (error) {
    setGlobalError(error as globalErrorType);
  } finally {
    removeLoading();
  }
}
